// The status card of the "Red flag warnings" tab: whether the mails are on for this
// project, the switch, and everything that decides whether a mail will actually go out.
//
// Warning mails are OFF for a project until someone adds a message and switches them on,
// and switching on never mails the past: the first mails are for red flags recorded from
// the next day. The switch-on dialog says so, because that is the moment the question
// "will everyone with a red flag get a mail now?" is asked. Switching off is confirmed
// too — red flags recorded while it is off are not mailed afterwards.
//
// The amber lines name every reason nothing would be sent even with the switch on, so an
// admin does not write five messages for a project where none can go out. The two that
// stop every mail (no mail server, the server switch) are repeated in the switch-on
// dialog, which then does not promise a first mail tomorrow.
//
// "Last 30 days" counts mails, not red flags (one mail can cover several), and is shown
// only to a caller who may see the mail trail — the server sends zeros to everyone else.

import { useState } from 'react';
import { AlertTriangle, Loader2, MailCheck } from 'lucide-react';
import Modal from '../Modal';
import { ModalFormErrorBanner } from '../ModalFormErrorBanner';
import { redFlagWarningMailsAPI, type RedFlagWarningConfig } from '../../services/api';
import { formatIsoDay } from '../../utils/dateHelpers';
import { apiMessage } from './warningMailText';

const AmberNotice = ({ text }: { text: string }) => (
  <p
    className="flex items-start gap-1.5 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900"
    role="status"
  >
    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
    <span>{text}</span>
  </p>
);

const ChipRow = ({ label, tone, items }: { label: string; tone: 'mailed' | 'not-mailed'; items: string[] }) => (
  <div className="flex flex-wrap items-center gap-1.5 text-sm">
    <span className="mr-1 text-gray-600">{label}:</span>
    {items.length === 0 ? (
      <span className="text-gray-400">None</span>
    ) : (
      items.map((item) => (
        <span
          key={item}
          className={`rounded-full border px-2 py-0.5 text-xs font-medium ${
            tone === 'mailed'
              ? 'border-green-200 bg-green-50 text-green-800'
              : 'border-gray-200 bg-gray-50 text-gray-600'
          }`}
        >
          {item}
        </span>
      ))
    )}
  </div>
);

export function WarningStatusCard({
  projectId, config, onChanged,
}: { projectId: number; config: RedFlagWarningConfig; onChanged: () => void | Promise<void> }) {
  const { settings, sendTime } = config;
  const [confirm, setConfirm] = useState<'on' | 'off' | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const time = sendTime || '10:00';
  const mailed = config.parameters.filter((p) => p.mailed).map((p) => p.label);
  const notMailed = config.parameters.filter((p) => !p.mailed).map((p) => p.label);
  const hasActiveMessage = config.steps.some((s) => s.isActive);

  // Nothing at all goes out while either of these holds, whatever the project's switch says.
  const sendBlockers: string[] = [];
  if (!config.mailConfigured) sendBlockers.push('The mail server is not set up — nothing will be sent.');
  if (!config.serverEnabled) {
    sendBlockers.push('Red flag warning mails are stopped by the server switch — nothing will be sent until it is turned back on.');
  }

  const notices: string[] = [...sendBlockers];
  if (config.rulesState === 'none') {
    notices.push('This project has no red-flag rules, so no red flag is raised and no mail will be sent. Set them up on Red Flag Rules.');
  }
  if (config.rulesState === 'unavailable') {
    notices.push('This project’s red-flag rules could not be read just now, so the parameters are not listed. Mails wait until the rules can be read.');
  }
  if (config.rulesState === 'ok' && mailed.length === 0) {
    notices.push('No parameter in this project counts toward Exit Reviews, so no mail will be sent. Change this on Red Flag Rules; it applies from next month.');
  }

  // The server refuses both; saying why here saves a round trip that can only fail.
  const blockedReason = settings.isActive
    ? null
    : !hasActiveMessage
      ? 'Add a message below before switching on.'
      : config.rulesState === 'none'
        ? 'Set up red-flag rules for this project before switching on.'
        : null;

  const close = () => {
    if (submitting) return;
    setConfirm(null);
    setError(null);
  };

  const apply = async () => {
    if (!confirm) return;
    setSubmitting(true);
    setError(null);
    try {
      await redFlagWarningMailsAPI.setActive(projectId, confirm === 'on');
      setConfirm(null);
      await onChanged();
    } catch (e) {
      setError(apiMessage(e, `Could not switch red flag warning mails ${confirm}. Please try again.`));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section aria-labelledby="rfw-status-title" className="space-y-3 rounded-lg border border-gray-200 bg-white p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <MailCheck className={`mt-0.5 h-5 w-5 shrink-0 ${settings.isActive ? 'text-green-600' : 'text-gray-400'}`} aria-hidden />
          <div>
            <h2 id="rfw-status-title" className="text-lg font-semibold text-gray-900">
              Red flag warning mails are{' '}
              <span className={settings.isActive ? 'text-green-700' : 'text-gray-500'}>{settings.isActive ? 'ON' : 'OFF'}</span>{' '}
              for this project
            </h2>
            <p className="mt-0.5 text-sm text-gray-500">
              Mails go out at {time} the next morning. Red flags are recorded overnight, once the day is complete.
            </p>
            {settings.isActive && settings.activeFrom && (
              <p className="mt-0.5 text-sm text-gray-500">
                Mailing red flags recorded from {formatIsoDay(settings.activeFrom)} onwards.
              </p>
            )}
          </div>
        </div>
        <div className="text-right">
          <button
            type="button"
            onClick={() => { setError(null); setConfirm(settings.isActive ? 'off' : 'on'); }}
            disabled={blockedReason !== null}
            className={
              settings.isActive
                ? 'rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50'
                : 'btn-brand rounded-lg px-4 py-2 text-sm font-medium text-white shadow-sm'
            }
          >
            {settings.isActive ? 'Switch off' : 'Switch on'}
          </button>
          {blockedReason && <p className="mt-1 text-xs text-gray-500">{blockedReason}</p>}
        </div>
      </div>

      {notices.map((text) => <AmberNotice key={text} text={text} />)}

      {config.rulesState === 'ok' && (
        <div className="space-y-1.5">
          <ChipRow label="Mailed" tone="mailed" items={mailed} />
          {notMailed.length > 0 && (
            <ChipRow label="Not mailed (does not count toward Exit Reviews)" tone="not-mailed" items={notMailed} />
          )}
        </div>
      )}

      {config.canReadTrail && (
        <p className="text-sm text-gray-700">
          Last 30 days: {config.summary30d.sent} {config.summary30d.sent === 1 ? 'mail' : 'mails'} sent
          {' · '}{config.summary30d.notSent} not sent · {config.summary30d.failed} failed
        </p>
      )}

      <details className="text-sm text-gray-600">
        <summary className="cursor-pointer font-medium text-gray-700">How it works</summary>
        <ul className="mt-2 list-disc space-y-1 pl-5">
          <li>
            The red flag number is counted the same way as on Exit Reviews: {config.startExit.minRedFlags} or more in 12
            months lists a person for exit review. The app lists every red flag, so it can show more.
          </li>
          <li>Reporting managers are not mailed — Exit Reviews does not list them.</li>
          <li>
            A red flag that is corrected or withdrawn later is not un-sent. The mail trail shows it as withdrawn.
          </li>
          <li>The mail goes to the employee’s official 1Office e-mail.</li>
        </ul>
      </details>

      <Modal
        isOpen={confirm !== null}
        onClose={close}
        title={confirm === 'off' ? 'Switch red flag warning mails off?' : 'Switch red flag warning mails on?'}
        size="md"
        footer={
          <>
            <button
              type="button"
              onClick={close}
              disabled={submitting}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => void apply()}
              disabled={submitting}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white btn-brand rounded-lg disabled:opacity-60"
            >
              {submitting && <Loader2 className="w-4 h-4 animate-spin" aria-hidden />}
              {confirm === 'off' ? 'Switch off' : 'Switch on'}
            </button>
          </>
        }
      >
        <div className="space-y-3 text-sm text-gray-700">
          {confirm === 'off' ? (
            <p>
              No mail is sent while this is off. Red flags recorded while it is off are not mailed later, even after
              you switch it back on.
            </p>
          ) : (
            <>
              {sendBlockers.length > 0 ? (
                <>
                  {sendBlockers.map((text) => <AmberNotice key={text} text={text} />)}
                  <p>Switching on applies to red flags recorded from tomorrow.</p>
                </>
              ) : (
                <p>First mails go out tomorrow at {time}, for red flags recorded from tomorrow.</p>
              )}
              <p className="text-gray-500">Red flags already recorded are not mailed.</p>
            </>
          )}
          {error && <ModalFormErrorBanner message={error} />}
        </div>
      </Modal>
    </section>
  );
}

export default WarningStatusCard;
